/**
 * Capture a pre-rendered share card (<ShareCardRender>) DOM node into a PNG
 * blob via html-to-image, then expose native-share / download helpers.
 *
 * Swapped away from Canvas 2D (which couldn't render KaTeX math or options).
 * The actual visual layout lives in components/ShareCardRender.tsx — that
 * node is mounted off-screen and snapshotted on demand.
 */

import { toBlob } from 'html-to-image';

export interface ShareCardInput {
  questionText: string;
  subject?: string;
  year?: number | string;
  topic?: string;
  url: string;
}

export type ShareCardOutcome = 'shared' | 'downloaded' | 'cancelled' | 'unsupported';

export interface ShareCardBundle {
  blob: Blob;
  /** Object URL — revoke with URL.revokeObjectURL when done. */
  objectUrl: string;
  filename: string;
  /** True if navigator.share with files is usable on this device. */
  canNativeShare: boolean;
}

/**
 * Snapshot an already-mounted ShareCardRender node.
 * The node MUST be in the DOM (not display:none) for fonts + KaTeX to
 * compute. Position it off-screen with `left: -99999px` instead.
 */
export async function captureShareCardNode(
  node: HTMLElement,
  filename?: string,
): Promise<ShareCardBundle> {
  // Give the browser a frame to settle — gives any async bits
  // (QR image load, KaTeX fonts, etc.) a chance to paint.
  await new Promise(r => requestAnimationFrame(() => r(null)));
  await document.fonts?.ready?.catch(() => {});

  const blob = await toBlob(node, {
    width: 1080,
    height: 1920,
    pixelRatio: 1,
    cacheBust: true,
    backgroundColor: '#0a0a0a',
  });
  if (!blob) throw new Error('html-to-image returned null');

  const name = filename || `lomi-${Date.now()}.png`;
  const nav: any = typeof navigator !== 'undefined' ? navigator : null;
  let canNativeShare = false;
  try {
    if (nav?.canShare && nav.share) {
      const file = new File([blob], name, { type: 'image/png' });
      canNativeShare = !!nav.canShare({ files: [file] });
    }
  } catch {
    /* canShare can throw on some browsers */
  }
  return {
    blob,
    objectUrl: URL.createObjectURL(blob),
    filename: name,
    canNativeShare,
  };
}

/** Invoke the OS native share sheet with the already-rendered blob. */
export async function nativeShareBundle(
  bundle: ShareCardBundle,
  input: ShareCardInput,
): Promise<Exclude<ShareCardOutcome, 'downloaded'>> {
  const nav: any = typeof navigator !== 'undefined' ? navigator : null;
  if (!nav?.share) return 'unsupported';
  const file = new File([bundle.blob], bundle.filename, { type: 'image/png' });
  try {
    await nav.share({
      files: [file],
      title: input.subject
        ? `ESSLCE practice · ${input.subject}`
        : 'ESSLCE practice',
      text: `Try this on Lomi 🍋  ${input.url}`,
    });
    return 'shared';
  } catch (err: any) {
    if (err?.name === 'AbortError') return 'cancelled';
    return 'unsupported';
  }
}

/** Trigger a regular download. Works on every browser. */
export function downloadBundle(bundle: ShareCardBundle): void {
  const a = document.createElement('a');
  a.href = bundle.objectUrl;
  a.download = bundle.filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}
